import React, { useEffect } from 'react';
import { X, ArrowRight } from 'lucide-react';

interface Ex { title: string; evidence: string; area: string; }
interface ExQuad { key: string; label: string; num: string; color: string; entries: Ex[]; }

const EXAMPLE: ExQuad[] = [
  {
    key: 'fortaleza', label: 'Fortalezas', num: '01', color: '#1F6F4A',
    entries: [
      { title: 'Negociación de contratos de largo plazo en sector minero', area: 'ESTRATEGIA',
        evidence: 'Cerré la renovación del contrato marco con Antamina por USD 180M anuales en 2024. Ahorro estimado de 8% sobre el baseline previo. Negociación directa con el comité ejecutivo de la mina y con CFO de la matriz.' },
      { title: 'Gestión de stakeholders externos complejos', area: 'NETWORKING',
        evidence: 'Lideré las relaciones con comunidades de Apurímac durante el conflicto social de Las Bambas en Q3 2025. Cero días de paro operativo en mi unidad mientras el promedio del sector fue de 14 días de cierre.' },
      { title: 'Lectura financiera de operaciones técnicas', area: 'ESTRATEGIA',
        evidence: 'Diseñé el modelo de costo unitario por tonelada que adoptó toda la división Latam en 2024. Hizo visibles márgenes ocultos en tres unidades de negocio del grupo.' },
    ],
  },
  {
    key: 'debilidad', label: 'Debilidades', num: '02', color: '#A50D27',
    entries: [
      { title: 'Comunicación con ejecutivos no técnicos del grupo', area: 'COMUNICACIÓN',
        evidence: 'Feedback recurrente del Country Manager: "tus presentaciones al board global son muy operativas, falta narrativa estratégica". Mencionado en mis últimas tres evaluaciones consecutivas.' },
      { title: 'Delegación de decisiones de complejidad media', area: 'EQUIPOS',
        evidence: 'Mi equipo directo (cinco gerentes) reporta en encuestas internas que valido decisiones que ellos podrían tomar. Identificado como punto de mejora en clima organizacional 2025.' },
      { title: 'Inglés ejecutivo en contextos formales', area: 'COMUNICACIÓN',
        evidence: 'Manejo técnico fluido pero pierdo precisión narrativa en presentaciones al board global de Tokio. Hace dos años no recibo coaching estructurado del idioma.' },
    ],
  },
  {
    key: 'oportunidad', label: 'Oportunidades', num: '03', color: '#0F4E8A',
    entries: [
      { title: 'Posición regional Andina en horizonte 2026', area: 'ESTRATEGIA',
        evidence: 'Vacancia anunciada por movimiento del actual GG Regional Andino para Q4. Mi perfil técnico calza; faltaría reforzar exposición a Chile y Argentina, donde no he tenido responsabilidad directa.' },
      { title: 'Liderazgo en agenda de transición energética del grupo', area: 'ESTRATEGIA',
        evidence: 'Mitsui anunció en febrero de 2026 un fondo de USD 2B para inversiones en transición energética en Latam. La división aún no tiene un champion claro de la agenda en la región.' },
      { title: 'Acceso directo a perspectiva del board en Tokio', area: 'NETWORKING',
        evidence: 'El programa de mentoría con tu mentor te da una puerta natural a la conversación con el comité ejecutivo global, históricamente lejana para gerencias regionales.' },
    ],
  },
  {
    key: 'amenaza', label: 'Amenazas', num: '04', color: '#8C5008',
    entries: [
      { title: 'Volatilidad regulatoria en sector minero peruano', area: 'ESTRATEGIA',
        evidence: 'Los cambios al régimen tributario minero anunciados por el nuevo gobierno pueden comprimir el margen operativo de mi unidad en 5-8% en 2027 según los modelos preliminares de finanzas.' },
      { title: 'Concentración geográfica de mi trayectoria', area: 'VISIBILIDAD',
        evidence: 'Dieciocho años en operaciones peruanas y ningún rol con responsabilidad internacional formal. Para una posición regional o global, puede leerse como falta de versatilidad ejecutiva.' },
    ],
  },
];

const FodaExampleModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  return (
    <div className="fw-ex-overlay" onClick={onClose}>
      <div className="fw-ex-modal" onClick={e => e.stopPropagation()}>
        <button className="fw-ex-close" title="Cerrar (Esc)" onClick={onClose}><X size={18} /></button>
        <div className="fw-ex-eyebrow">Referencia</div>
        <h2 className="fw-ex-title">Ejemplo de <span>FODA</span></h2>
        <p className="fw-ex-desc">
          Una referencia de cómo se ve un FODA bien construido. Cada entrada tiene un título preciso y una evidencia
          con datos, situaciones y resultados específicos. <strong>El tuyo no tiene que verse igual</strong> — solo
          tener el mismo nivel de concreción.
        </p>

        {/* Resumen 2x2 */}
        <div className="fw-ex-grid">
          {EXAMPLE.map(q => (
            <div className="fw-ex-cell" key={q.key} style={{ '--qc': q.color } as React.CSSProperties}>
              <span className="fw-ex-num">{q.num}</span>
              <div className="fw-ex-cell-title">{q.label}</div>
              <ul className="fw-ex-list">
                {q.entries.map((e, i) => <li key={i}>{e.title}</li>)}
              </ul>
            </div>
          ))}
        </div>

        <div className="fw-ex-eyebrow fw-ex-eyebrow--sep">Detalle de cada entrada</div>
        {EXAMPLE.map(q => (
          <section className="fw-ex-section" key={q.key} style={{ '--qc': q.color } as React.CSSProperties}>
            <div className="fw-ex-section-label">{q.num} — {q.label.toUpperCase()}</div>
            <div className="fw-ex-cards">
              {q.entries.map((e, i) => (
                <div className="fw-ex-card" key={i}>
                  <div className="fw-ex-card-title">{e.title}</div>
                  <div className="fw-ex-card-ev">{e.evidence}</div>
                  <span className="fw-area-chip">{e.area}</span>
                </div>
              ))}
            </div>
          </section>
        ))}

        <div className="fw-ex-foot">
          <button className="foda-btn foda-btn--ink" onClick={onClose}>Volver a mi FODA <ArrowRight size={16} /></button>
        </div>
      </div>
    </div>
  );
};

export default FodaExampleModal;
